/* eslint-disable no-unused-vars */
import dateFns from 'date-fns';
import { formatDate } from '../utils/formatDate';
import { intervalsOverlap } from '../utils/intervalsOverlap';

/**
 * Verifica se um profissional possui períodos de visita agendados (periodsForVisit)
 * que se sobrepõem aos seus períodos de descanso (periodsForRest), AGREGANDO
 * todos os períodos de todos os schedulingEvents para o mesmo profissional.
 *
 * @param {Array<Object>} schedulingEvents - Array de objetos de agendamento.
 * Cada objeto deve ter:
 * - professionalName (string)
 * - periodsForVisit (Array<{start: string, end: string}>) - Visitas agendadas.
 * - periodsForRest (Array<{start: string, end: string}>) - Descansos agendados.
 * @returns {Array<string>} - Array de mensagens de erro para conflitos agregados de escala.
 */
function check(schedulingEvents) {
  const detailedErrors = [];
  const summaryErrors = [];

  // =================================================================
  // AGREGADOR: Map para armazenar todos os períodos por profissional
  // =================================================================
  const professionalPeriodsMap = new Map();

  // ======================================================
  // 1. AGREGAÇÃO: Unir todos os períodos por profissional
  // ======================================================
  for (const event of schedulingEvents) {
    const { formVisits, professionalName, periodsForVisit, periodsForRest } = event;
    const { professionalId } = formVisits;

    if (!professionalPeriodsMap.has(professionalId)) {
      professionalPeriodsMap.set(professionalId, {
        professionalName,
        visits: [],
        rests: [],
        conflictingVisits: new Set() // Conjunto para evitar relatar a mesma visita conflitante várias vezes
      });
    }

    const data = professionalPeriodsMap.get(professionalId);
    data.visits.push(...periodsForVisit);
    data.rests.push(...periodsForRest);
  }

  // =====================================================
  // 2. COMPARAÇÃO: Comparar Visitas e Descansos Agregados
  // =====================================================
  for (const [_professionalId, data] of professionalPeriodsMap.entries()) {
    const { visits, rests, conflictingVisits } = data;
    const { professionalName } = data;

    // Iterar sobre todas as visitas agregadas do profissional
    for (const periodVisit of visits) {
      const visitStart = new Date(periodVisit.start);
      const visitEnd = new Date(periodVisit.end);

      const visitKey = `${periodVisit.start}|${periodVisit.end}`; // Chave única para a visita

      // Iterar sobre todos os descansos agregados do profissional
      for (const periodRest of rests) {
        const restStart = new Date(periodRest.start);
        const restEnd = new Date(periodRest.end);

        // ===================================
        // 3. Verificar Sobreposição
        // ===================================
        if (intervalsOverlap(visitStart, visitEnd, restStart, restEnd)) {
          if (conflictingVisits.has(visitKey)) return;

          const errorMessage =
            `O profissional ${professionalName} possui uma visita agendada (${formatDate(
              periodVisit.start
            )} das ${dateFns.format(visitStart, 'HH:mm')} às ${dateFns.format(
              visitEnd,
              'HH:mm'
            )}) ` +
            `que se sobrepõe a um período de descanso (${formatDate(
              periodRest.start
            )} das ${dateFns.format(restStart, 'HH:mm')} às ${dateFns.format(restEnd, 'HH:mm')}).`;

          const aggregateErrorsMsg = `O profissional ${professionalName} possui visita(s) agendada(s) que se sobrepõe(m) a um período de descanso.`;
          if (!summaryErrors.find(err => err === aggregateErrorsMsg)) {
            summaryErrors.push(aggregateErrorsMsg);
          }

          detailedErrors.push(errorMessage);
          conflictingVisits.add(visitKey);

          // Uma vez que foi encontrado UM descanso conflitante para esta
          // visita, podemos parar de verificar os outros descansos e ir para a
          // próxima visita.
          break;
        }
      }
    }
  }

  return { detailedErrors, summaryErrors };
}

export { check };
